// NPM Dependencies
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

// Config & Constants
import { customLocation, dateFilterConfig, destinationField, MRB_FILTERS_CONSTANTS, filterTypes, TRAVEL_REQUEST_CONFIG,
	ICON_MAP, MRB_FILTER_CONFIG, DEFAULT_SORT_BY_TYPE, REQUEST_TYPE_ADOBE_PREFIX, BOOKING_TITLE_MAP, FLIGHT_TYPE_TITLE_MAP, getSearchKey } from '../manageRequestBookings/config';
import { APPLICATION_CONSTANTS, FILTER_CONSTANTS, MRB_CONSTANTS, LOB_TYPES, ADOBE_PAGE_NAMES, ADOBE_EVENT_VALUES, SHORTLIST_SHARE_CONSTANTS } from 'AppConstants';

// Utils
import { updateFltClickCount, updateAppliedFilterObj } from 'UtilityComponents/filterUtils';
import { getPluralText, saveServerLogs } from './Util';
import { pushClickToAdobe } from 'analyticsSdk';

const isPendingOrExpired = (requestType) =>
	requestType === MRB_CONSTANTS.PENDING_REQUEST_TYPE || requestType === MRB_CONSTANTS.EXPIRED_REQUEST_TYPE;

/**
 * get date in epoch for earlier dates based on noOfDays
 * @author - mmt9855
 * @param {Object} dateObj - date object
 * @param {Number} noOfDays - number of days to be subtracted
 * @return {Number} - updated date in milliseconds
 */
export const getEarlierDate = (dateObj, noOfDays) => {
	try {
		return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate() - noOfDays).getTime();
	} catch (error) {
		saveServerLogs(error && error.stack ? error.stack : error, 'error');
	}
};

/**
 * get date in epoch for the end of the day based on the number of days
 * @author - mmt9855
 * @param {Object} dateObj - date object
 * @param {Number} noOfDays - number of days to be subtracted
 * @return {Number} - updated date in milliseconds
 */
export const getEndingDate = (dateObj, noOfDays) => {
	try {
		return Number(moment(dateObj).subtract(noOfDays, 'days').endOf('day').format('x'));
	} catch (error) {
		saveServerLogs(error && error.stack ? error.stack : error, 'error');
	}
};

/**
 * get date in epoch based on noOfDays in advance
 * @author - mmt9855
 * @param {Object} dateObj - date object
 * @param {Number} noOfDays - number of days to be subtracted
 * @return {Number} - updated date in milliseconds
 */
export const getAdvanceDate = (dateObj, noOfDays) => {
	try {
		return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate() + noOfDays).getTime();
	} catch (error) {
		saveServerLogs(error && error.stack ? error.stack : error, 'error');
	}
};

const getInitDateQuery = (startKey, endKey, requestType) => {
	const dateQuery = {};
	const dateObj = APPLICATION_CONSTANTS.TODAY;
	dateQuery[startKey] = getEarlierDate(dateObj, isPendingOrExpired(requestType) ? MRB_CONSTANTS.PENDING_NO_OF_DAYS : MRB_CONSTANTS.UPCOMING_BOOKING_DATE_RANGE);
	dateQuery[endKey] = dateObj.getTime();
	return dateQuery;
};

export const getDayStartDate = (dateObj, day)=>{
	try {
		const start = moment(dateObj).add(day, 'days').startOf('day').format('x');
		return start;
	} catch (err) {
		saveServerLogs(err && err.stack);
	}
};
export const getDayEndDate = (dateObj, day)=>{
	try {
		const end = moment(dateObj).add(day, 'days').endOf('day').format('x');
		return end;
	} catch (err) {
		saveServerLogs(err && err.stack);
	}
};

/**
 * get date filter and query object
 * @author - mmt9855
 * @param {String} requestType - request type based on tab
 * @return {Object} - get date filter and query object
 */
export const createDateFiltersOptions = (key = '', requestType) => {
	const dateObj = APPLICATION_CONSTANTS.TODAY;
	const dateQuery = getInitDateQuery('startDate', 'endDate', requestType);
	const options = [];
	const config = TRAVEL_REQUEST_CONFIG[key];
	const len = config.length;
	for (let i = 0; i < len; i++) {
		const option = { ...dateFilterConfig };
		option.index = i;
		option.label = config[i].label;
		option.adobeVal = config[i].adobeVal || '';
		const fromDate = key === MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL && (typeof config[i].startDay !== 'undefined' ? getDayStartDate(dateObj, config[i].startDay) : dateObj.getTime());
		const toDate = key === MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL && getDayEndDate(dateObj, config[i].endDay);
		option.start = fromDate ? fromDate : getEarlierDate(dateObj, config[i].startDay);
		option.end = toDate ? toDate : typeof config[i].endDay === 'undefined' ? dateObj.getTime() :
			getEndingDate(dateObj, config[i].endDay);
		options.push(option);
	}
	/* if (true) {
		dateQuery.startDate = getEarlierDate(dateObj, MRB_CONSTANTS.PENDING_NO_OF_DAYS);
		dateQuery.endDate = dateObj.getTime();
	} else {
		dateQuery.startDate = dateObj.getTime();
		dateQuery.endDate = getAdvanceDate(dateObj, MRB_CONSTANTS.UPCOMING_NO_OF_DAYS);
	} */
	return {
		options,
		dateQuery
	};
};

/**
 * get all the filters based on request type
 * @author - mmt9855
 * @param {Object} filtersConfig - filter config
 * @param {String} requestType - request type based on tab
 * @return {Array} - get all the filters in array object
 */
export const getFilters = (filtersConfig, requestType = '') => {
	const filters = cloneDeep(filtersConfig);
	const filtersData = [];
	for (let key in filters) {
		switch (key) {
			case MRB_FILTERS_CONSTANTS.APPROVER:
				isPendingOrExpired(requestType) && filtersData.push(filters[key]);
				break;
			case MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL:
			case MRB_FILTERS_CONSTANTS.DATE_OF_BOOKING: {
				const { options: newOptions = [] } = createDateFiltersOptions(key);
				filters[key].options = [...newOptions, ...filters[key].options];
				const isUpcoming = requestType === MRB_CONSTANTS.UPCOMING_REQUEST_TYPE;
				if (isUpcoming || key === MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL) {
					filtersData.push(filters[key]);
				}
				break;
			}
			case MRB_FILTERS_CONSTANTS.DATE_OF_REQUEST: {
				const { options: newOptions = [] } = createDateFiltersOptions(key, requestType);
				filters[key].options = [...newOptions, ...filters[key].options];
				isPendingOrExpired(requestType) && filtersData.push(filters[key]);
				break;
			}
			case MRB_FILTERS_CONSTANTS.TRAVEL_AGE: {
				isPendingOrExpired(requestType) && filtersData.push(filters[key]);
				break;
			}
			case MRB_FILTERS_CONSTANTS.TRIP_TYPE: {
				const opts = filters[key].options;
				filters[MRB_FILTERS_CONSTANTS.TRIP_TYPE].options = requestType === MRB_CONSTANTS.UPCOMING_REQUEST_TYPE ?
					filters[key].options.filter((item) => item.value !== MRB_FILTERS_CONSTANTS.FLIGHT_MOD_TYPE) :
					opts;
				filtersData.push(filters[key]);
				break;
			}
			default:
				filtersData.push(filters[key]);
		}
	}
	return filtersData;
};

/**
 * get initial query object for the request
 * @author - mmt9855
 * @param {String} requestType - request type based on tab
 * @param {String} value - search value if any
 * @return {Object} - returns query for the data and count api
 */
export const getInitialQuery = (requestType, value) => {
	const dateQuery = getInitDateQuery('startDate', 'endDate', requestType);
	const sort = DEFAULT_SORT_BY_TYPE[requestType];
	const searchKey = getSearchKey(requestType);

	return {
		...dateQuery,
		...(value && { [searchKey]: value }),
		...(sort && { sort: { ...sort } })
	};
};

/**
 * Create updatedQuery & updatedFilter object for checkbox filters
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Object} queryOption - queryOptions containing {checked ad value}
 * @param {Object} index  - queryOptions containing {checked ad value}
 * @param {Object} filters - queryOptions containing {checked ad value}
 * @return {Object} - Updated query and filters
 */
const setCheckboxQuery = (query = {}, filter = {}, queryOption = {}, index, filters) => {
	const { key, singleSelect = false } = filter;
	const updatedQuery = cloneDeep(query);
	const { checked, value } = queryOption;
	const updatedFilter = cloneDeep(filter);
	if (checked) {
		updatedQuery[key] = updatedQuery[key] && Array.isArray(updatedQuery[key]) && !singleSelect ?
			[...updatedQuery[key], value] : value || value === 0 ? [value] : [];
	} else {
		updatedQuery[key] = updatedQuery[key].filter((item) => item !== value);
		if (updatedQuery[key].length === 0) {
			delete updatedQuery[key];
		}
	}

	updatedFilter.showClear = (!updatedFilter.hideClear) && updatedQuery[key] && updatedQuery[key].length > 0;
	const updatedFilters = cloneDeep(filters);
	updatedFilters[index] = updatedFilter;
	if (key === MRB_CONSTANTS.TRIP_TYPE) {
		const { fltrs, newQuery } = addNewField(updatedFilters, updatedFilter, updatedQuery);
		return { updatedQuery: newQuery, updatedFilters: fltrs };
	}
	return { updatedQuery, updatedFilters };
};

/**
 * Create updatedQuery & updatedFilter object for date range filters
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Object} queryOption - queryOptions containing {checked ad value}
 * @return {Object} - Updated query and filter
 */
const setDateRangeQuery = (query = {}, filter = {}, queryOption = {}) => {
	const appliedFilter = [];
	const { listHead = '' } = queryOption;
	updateAppliedFilterObj(appliedFilter, [queryOption]);
	const updatedQuery = cloneDeep(query);
	const dateRange = appliedFilter[0] || {};
	const { start = '', end = '' } = dateRange;
	const { startKey = '', endKey = '' } = filter;
	updateFltClickCount(filter, queryOption.checked);
	return {
		updatedQuery: { ...updatedQuery, [startKey]: start, [endKey]: end },
		updatedFilter: { ...filter, selectedHead: listHead }
	};
};
/**
 * Create updatedQuery & updatedFilter object for date range filters
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Object} queryOption - queryOptions containing {checked ad value}
 * @return {Object} - Updated query and filter
 */
const setAgeValue = (query = {}, filter = {}, queryOption = {}) => {
	const { startKey, endKey } = filter;
	const updatedQuery = cloneDeep(query);
	updatedQuery[startKey] = queryOption[startKey];
	updatedQuery[endKey] = queryOption[endKey];
	return {
		updatedQuery,
		updatedFilter: cloneDeep(filter)
	};
};

/**
 * Create updatedQuery & updatedFilter object for input field filter
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {String} queryValue - queryValue containing the text user has entered
 * @param {Object} index - current index for the filter
 * @param {Object} filters - all the filters
 * @param {String} travelKey - travelKey in case of source and destination filter
 * @return {Object} - Updated query and filter
 */
const setInputFieldQuery = (query = {}, filter = {}, queryValue = '', index, filters, travelKey) => {
	const { key = '' } = filter;
	const updatedQuery = cloneDeep(query);
	if (travelKey) {
		updatedQuery[key] = {
			...updatedQuery[key],
			[travelKey]: queryValue
		};
	} else {
		updatedQuery[key] = {
			stayLocation: queryValue
		};
	}
	const updatedFilters = cloneDeep(filters);
	updatedFilters[index] = filter;
	return {
		updatedQuery,
		updatedFilters: updatedFilters
	};
};

/**
 * Create updatedQuery & updatedFilter object for dropdown filter
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Array} queryValue - queryValue containing
 * @param {Object} index - current index for the filter
 * @param {Object} filters - all the filters
 * @return {Object} - Updated query and filter
 */
const setDropDownQuery = (query = {}, filter = {}, queryValue = [], index, filters) => {
	const key = filter.key;
	const updatedQuery = cloneDeep(query);
	const updatedFilter = cloneDeep(filter);

	if (queryValue && queryValue.length > 0) {
		updatedQuery[key] = queryValue.map((item) => item.value);
	} else {
		updatedQuery[key] && delete updatedQuery[key];
	}

	updatedFilter.showClear = updatedQuery[key] && updatedQuery[key].length > 0;
	const updatedFilters = cloneDeep(filters);
	updatedFilters[index] = updatedFilter;
	return { updatedQuery, updatedFilters };
};

/**
 * Single-select dropdown that routes the selected email to query.searchQuery
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Object|null} queryValue - selected option (single) or null on clear
 * @param {number} index - current index for the filter
 * @param {Array} filters - all the filters
 * @return {Object} - Updated query and filters
 */
const setTravellerSearchQuery = (query = {}, filter = {}, queryValue = null, index, filters) => {
	const updatedQuery = cloneDeep(query);
	const updatedFilter = cloneDeep(filter);
	const selectedEmail = queryValue?.value;

	if (selectedEmail) {
		updatedQuery.searchQuery = selectedEmail;
		updatedFilter.value = queryValue;
	} else {
		delete updatedQuery.searchQuery;
		updatedFilter.value = '';
	}

	updatedFilter.showClear = !!selectedEmail;
	const updatedFilters = cloneDeep(filters);
	updatedFilters[index] = updatedFilter;
	return { updatedQuery, updatedFilters };
};

/**
 * Create updatedQuery & updatedFilter object which is used in API Call
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filter - selected filter
 * @param {Object} queryData - queryOptions containing {option ad value}
 * @param {Object} index - current index for the filter
 * @param {Object} filters - all the filters
 * @param {String} travelKey - travelKey for the location filters
 * @return {Object} - Updated query and filter for selected filter
 */
export const getFilterQuery = (query = {}, filter = {}, queryData = {}, index, filters, travelKey) => {
	const key = filter.key;
	const { value: queryValue, option: queryOption } = queryData;
	switch (key) {
		case MRB_CONSTANTS.POLICY:
		case MRB_CONSTANTS.TRIP_TYPE: {
			return setCheckboxQuery(query, filter, queryOption, index, filters);
		}
		case MRB_CONSTANTS.APPROVER: {
			return setDropDownQuery(query, filter, queryValue, index, filters);
		}
		case MRB_FILTERS_CONSTANTS.TRAVELLER: {
			return setTravellerSearchQuery(query, filter, queryValue, index, filters);
		}
		case filterTypes.location:
		case filterTypes.customLocation:
			return setInputFieldQuery(query, filter, queryValue, index, filters, travelKey);
		case FILTER_CONSTANTS.DATE_FILTER: {
			return setDateRangeQuery(query, filter, queryValue, index, filters);
		}
		default: {
			return query;
		}
	}
};

/**
 * Clear Checkbox Filtes and Update query
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {string} key - key of the filter
 * @return {Object} - Updated query and filter for selected filter
 */
const clearCheckBoxFilter = (query = {}, key) => {
	const updatedQuery = cloneDeep(query);
	updatedQuery[key] && delete updatedQuery[key];
	if (key === MRB_CONSTANTS.TRIP_TYPE) {
		updatedQuery?.[filterTypes.location] && delete updatedQuery[filterTypes.location];
	}
	return {
		updatedQuery
	};
};

/**
 * Clear Date Filtes and Update query
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} filtersState - current reducer filter object
 * @return {Object} - Updated query and filter for selected filter
 */
const clearDateFilter = (query = {}, filtersState, index) => {
	let updatedQuery = cloneDeep(query);
	const dateFlt = filtersState[index];
	const { startKey, endKey } = filtersState[index];
	const isPageScopeDate = startKey === 'startDate' && endKey === 'endDate';
	if (dateFlt) {
		dateFlt.options.forEach((opts) => {
			opts.checked = false;
			if (opts.type === FILTER_CONSTANTS.CUSTOM_DATE_FILTER) {
				opts.start = '';
				opts.end = '';
				opts.dateValue = '';
			}
		});
		dateFlt.showClear = false;
		delete (updatedQuery[startKey]);
		delete (updatedQuery[endKey]);
	}
	if (isPageScopeDate) {
		updatedQuery = { ...updatedQuery, ...getInitDateQuery(startKey, endKey) };
	}
	return {
		updatedQuery,
		updatedFilter: dateFlt
	};
};

/**
 * Clear Filtes and Update query
 * @author - mmt9855
 * @param {Object} query - previous query object
 * @param {Object} clearedFilter - filter which needs to be cleared
 * @param {String} filtersState - request type which is needed for clear date filter
 * @return {Object} - Updated query and filter for selected filter
 */
export const clearFilterNQuery = (query = {}, clearedFilter = {}, filtersState, index) => {
	const key = clearedFilter.key;

	let updatedFilter = cloneDeep(clearedFilter);
	updatedFilter.showClear = false;

	switch (key) {
		case MRB_CONSTANTS.POLICY:
		case MRB_CONSTANTS.TRIP_TYPE: {
			return {
				updatedFilter,
				...clearCheckBoxFilter(query, key)
			};
		}
		case FILTER_CONSTANTS.DATE_FILTER: {
			return clearDateFilter(query, filtersState, index);
		}
		case MRB_CONSTANTS.APPROVER: {
			updatedFilter[key] = { ...clearedFilter, showClear: false };
			const updatedQuery = cloneDeep(query);
			updatedQuery[key] && delete updatedQuery[key];

			return {
				updatedFilter,
				updatedQuery
			};
		}
		case MRB_FILTERS_CONSTANTS.TRAVELLER: {
			updatedFilter = { ...updatedFilter, value: '', showClear: false };
			const updatedQuery = cloneDeep(query);
			delete updatedQuery.searchQuery;

			return {
				updatedFilter,
				updatedQuery
			};
		}
		default: {
			return {
				updatedFilter,
				updatedQuery: query
			};
		}
	}
};

/**
 * add options based on api call for approver filter
 * @author - mmt9855
 * @param {Array} filters - all the present filter
 * @param {String} filterKey - selected filter
 * @param {Array} optionsData - options array containing {value and la}
 * @return {Array} - updated filters with options added for the approver filter
 */
export const formatFiltersOptions = (filters = [], filterKey = '', optionsData = []) => {
	let filterData = filters;
	if (optionsData) {
		filterData = cloneDeep(filterData);
		filterData[filterKey].options = [...optionsData];
	}
	return filterData;
};

const removeLocationFilter = (fltrs, newQuery, arrIndex) => {
	fltrs.splice(arrIndex, 1);
	if (newQuery['location']) {
		delete newQuery['location'];
	}
};

/**
 * add new filter in case of any trip type is selected
 * @author - mmt9855
 * @param {Array} changedFilters - all the present filter
 * @param {Object} filter - selected filter
 * @param {Object} updatedQuery - updatedQuery containing {option and value}
 * @return {Array} - updated filters with addition of new filter for location
 */
export const addNewField = (changedFilters = [], filter = {}, updatedQuery = {}) => {
	const fltkey = filter.key;
	const selectedValues = updatedQuery[fltkey];
	const fltrs = [...changedFilters];
	let locfltKey = -1;
	const fltrsLen = fltrs.length;
	for (let i = 0; i < fltrsLen; i++) {
		if (fltrs[i].key === customLocation.key) {
			locfltKey = i;
			break;
		}
	}
	const isLocationEnabled = locfltKey > 0;
	const newQuery = cloneDeep(updatedQuery);
	if (!selectedValues && isLocationEnabled) {
		removeLocationFilter(fltrs, newQuery, locfltKey);
		return {
			fltrs,
			newQuery
		};
	}
	const onlyselVal = selectedValues && selectedValues[0];
	if (!isLocationEnabled && selectedValues && selectedValues.length === 1 &&
		onlyselVal !== MRB_FILTERS_CONSTANTS.REQUISTION_TYPE) {
		fltrs[changedFilters.length] = onlyselVal === MRB_FILTERS_CONSTANTS.HOTEL_TYPE ?
			destinationField : customLocation;
	} else if (selectedValues && selectedValues.length > 1) {
		const htlsel = selectedValues.some((item) => item === MRB_FILTERS_CONSTANTS.HOTEL_TYPE);
		const cabsel = selectedValues.some((item) => item === MRB_FILTERS_CONSTANTS.CAB_TYPE);
		const requisitionSelected = selectedValues.some((item) => item === MRB_FILTERS_CONSTANTS.REQUISTION_TYPE);
		if (!isLocationEnabled && !(htlsel || requisitionSelected || cabsel)) {
			fltrs[changedFilters.length] = customLocation;
		} else if (isLocationEnabled) {
			removeLocationFilter(fltrs, newQuery, locfltKey);
		}
	}
	return {
		fltrs,
		newQuery
	};
};

/**
 * Format Filters with count data
 * @author - mmt9855
 * @param {Object} countData - countData from API
 * @param {Array} filters - all filters for a particular report
 * @return {Array} - Updated Filters with count
 */
export const formatFilterCounts = (countData, filters = []) => {
	let filterData = cloneDeep(filters);
	filterData.forEach((filter) => {
		const { key = '' } = filter;
		if (key === MRB_CONSTANTS.POLICY || key === MRB_CONSTANTS.TRIP_TYPE) {
			const countVals = countData?.[key];
			filter?.options?.forEach((option) => {
				option.count = countVals?.[option?.countKey];
			});
		}
	});
	return filterData;
};

const getEndTravelDate = (item) => {
	const { lobData, tripType } = item;
	if (!lobData) return '';
	if (tripType === LOB_TYPES.HOTEL) return lobData.checkOutDate || '';
	if (tripType === LOB_TYPES.FLIGHT && lobData.flightReturnDeptDate) return lobData.flightReturnDeptDate;
	return '';
};

const formatFlightDuration = (mins) => {
	const totalMins = Math.max(0, Math.round(Number(mins) || 0));
	if (!totalMins) return '';
	const hrs = Math.floor(totalMins / 60);
	const remaining = totalMins % 60;
	return `${String(hrs).padStart(2, '0')}h ${String(remaining).padStart(2, '0')}m`;
};

const formatEpochToTime = (epoch) => {
	const value = Number(epoch);
	if (!Number.isFinite(value) || value <= 0) return '';
	const m = moment(value);
	return m.isValid() ? m.format('HH:mm') : '';
};

const buildFlightLeg = (journey = {}) => ({
	source: journey.departureAirportCode || '',
	destination: journey.arrivalAirportCode || '',
	sourceCity: journey.departureCityName || '',
	destinationCity: journey.arrivalCityName || '',
	sourceAirportName: journey.departureAirportName || '',
	destinationAirportName: journey.arrivalAirportName || '',
	sourceTerminal: journey.departureTerminalInfo || '',
	destinationTerminal: journey.arrivalTerminalInfo || '',
	departureTime: formatEpochToTime(journey.departureTime),
	arrivalTime: formatEpochToTime(journey.arrivalTime),
	duration: formatFlightDuration(journey.durationInMins),
	stops: Number.isFinite(Number(journey.stops)) ? Number(journey.stops) : 0
});

const getFlightLegs = (lobData = {}) => {
	const { journeyDetails } = lobData || {};
	if (!Array.isArray(journeyDetails) || journeyDetails.length === 0) return [];
	return journeyDetails.map(buildFlightLeg);
};

const getBookingTitle = (item) => {
	if (item.tripType === LOB_TYPES.FLIGHT) {
		return FLIGHT_TYPE_TITLE_MAP[item.lobData?.flightType] || BOOKING_TITLE_MAP[LOB_TYPES.FLIGHT];
	}
	return BOOKING_TITLE_MAP[item.tripType] || '';
};

const formatListing = (data = [], requestType, isRequisitionService) => data.flatMap((item = {}) => {
	const isUpcomingBooking = requestType === MRB_CONSTANTS.UPCOMING_REQUEST_TYPE;
	const isRequisitionWrapper = item.tripType === LOB_TYPES.REQUISITION;

	if (isUpcomingBooking && isRequisitionWrapper && item.services?.length) {
		return formatListing(item.services, requestType, isRequisitionService);
	}

	const apprItem = {};
	apprItem.isOpen = true;
	const reqsnLabel = MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.REQUISTION_TYPE]?.label;
	const baseLocation = isRequisitionWrapper && !isUpcomingBooking ?
		{ source: reqsnLabel, subText: item.requisitionId } :
		item.location || {};
	const suppressParentOop = isRequisitionWrapper && isUpcomingBooking;
	const mappedTripType = ICON_MAP[item.tripType];
	const flightLegs = isUpcomingBooking && item.tripType === LOB_TYPES.FLIGHT ? getFlightLegs(item.lobData) : [];
	apprItem.location = {
		...baseLocation,
		inPolicy: suppressParentOop ? true : item.inPolicy,
		oopReasons: suppressParentOop ? [] : (item.oopReasons || []),
		isReturnJourney: item.lobData?.flightType === SHORTLIST_SHARE_CONSTANTS.FLIGHT_TYPES.ROUND_TRIP,
		tripType: mappedTripType,
		legs: flightLegs
	};
	apprItem.tripType = mappedTripType;
	apprItem.services = item.services ? formatListing(item.services, requestType, isRequisitionWrapper) : [];
	apprItem.id = item.id;
	apprItem.bookingId = isRequisitionWrapper ?
		{ title: '', bookingId: item.bookingId || (isUpcomingBooking && reqsnLabel) || '' } :
		{ title: getBookingTitle(item), bookingId: item.bookingId || '' };

	const travelDate = item.dateOfTravelling || (!isRequisitionWrapper && '-');
	const endDate = getEndTravelDate(item);
	apprItem.bookingDateData = {
		travelDate: item.dateOfBooking || '',
		travelTime: item.timeOfBooking || ''
	};
	apprItem.travelDateData = {
		travelDate,
		endDate,
		travelTime: item.timeOfTravelling || ''
	};

	apprItem.ageInHrs = item.ageInHrs;
	apprItem.formattedAge = item.formattedAge;
	apprItem.estimatedCost = item.estimatedCost;
	apprItem.inPolicy = item.inPolicy;
	apprItem.status = item.bookingStatus || item.status || (!isRequisitionWrapper && '-');
	apprItem['dateOfTravelling'] = item.dateOfTravelling;
	const traveLen = item.travellers?.length;
	const { firstName = '', lastName = '', emailID = '' } = item.travellers?.[0] || {};
	const travellerName = traveLen > 0 ? `${firstName} ${lastName}${traveLen > 1 ? ` +${traveLen - 1}` : ''}` : '';
	apprItem.traveller = { name: travellerName, email: emailID };
	apprItem.approver = { name: item.approver?.name || '', email: item.approver?.email || '' };
	if (isRequisitionService) {
		apprItem.bodyCellProps = {
			padding: '0px 16px'
		};
	}
	if (isRequisitionWrapper) {
		apprItem.bodyCellProps = {
			noBdr: true
		};
	}

	const showAction = (!isRequisitionWrapper && !isRequisitionService) || (isUpcomingBooking && isRequisitionService) || (isRequisitionWrapper && !isUpcomingBooking);

	apprItem.action = !showAction ? false : {
		bookingId: item.bookingId,
		url: item.actionUrl,
		bookingStatus: item.bookingStatus,
		isRequisition: !!item.requisition,
		requisitionId: item.requisition ? item.id : undefined,
		workflowId: item.requisition ? undefined : item.id,
		disableApprovalKeys: item.disableApprovalKeys
	};
	return apprItem;
});

export const formatPendingData = (data = [], currentList = [], requestType) => {
	const retData = { data: [], error: false, loading: false, hasMore: false };
	if (data.length > 0) {
		retData.hasMore = true;
	}
	currentList = currentList.concat(formatListing(data, requestType));
	retData.data = currentList;
	return retData;
};

/**
 * @function pushManageBookingsClick
 *
 * @author - MMT11933
 * @description - Push click event to Adobe analytics SDK
 * @param {string} eventValue - The value of the event
 * @param {object} extraOptions - The extra options for the event
 */
export const pushManageBookingsClick = (eventValue, extraOptions = {}) => {
	pushClickToAdobe(ADOBE_PAGE_NAMES.MANAGE_BOOKINGS, eventValue, extraOptions);
};

/**
 * @function pushManageBookingsFilterClick
 *
 * @author - MMT11933
 * @description - Push filter click event to Adobe analytics SDK
 * @param {string} requestType - The type of the request
 * @param {Object} filter - The filter object
 * @param {Object} option - The option object
 * @param {Object} value - The value object
 */
export const pushManageBookingsFilterClick = (requestType, filter, option, value) => {
	let eventValue = '';
	switch (filter.key) {
		case 'policyType':
		case 'tripType': {
			const prefix = REQUEST_TYPE_ADOBE_PREFIX[requestType] || ADOBE_EVENT_VALUES.MANAGE_BOOKING.ALL_BOOKINGS_CLICK;
			eventValue = value ? prefix + option?.adobeVal : '';
			break;
		}
		case 'dateRange':
			eventValue = value?.adobeVal;
			break;
	}

	eventValue && pushManageBookingsClick(eventValue);
};
